import { html } from '../../html.js';
import type { VNode } from '../../html.js';
import { OUTCOMES } from '../api.js';
import type { Outcome, SeriesPoint } from '../api.js';
import { Scroller, fmtInt } from './bits.js';

/**
 * The call series, as inline SVG.
 *
 * Hand-drawn rather than charted by a library, for the reason every other asset
 * on these pages is local: nothing may be fetched from a CDN, and a chart library
 * would be the largest dependency in the bundle to draw eleven rectangles.
 *
 * The honesty rule that shapes it: a day with no calls is drawn as a ZERO, not
 * as a gap. The server zero-fills the window, and every day in the series gets a
 * column plus a baseline tick — so a quiet Sunday reads as a quiet Sunday and a
 * backend that stopped writing usage rows reads as a wall of zeroes rather than
 * as a chart that helpfully skips to the next data point.
 *
 * Bars are stacked by outcome because the outcome split is the whole story: 400
 * calls that were all `error` and 400 that were all `ok` are the same bar height
 * and opposite facts.
 */

const H = 132;
const PAD_TOP = 8;
const PAD_BOTTOM = 22;
const COL = 26;
const GAP = 6;

export const OUTCOME_LABEL: Record<Outcome, string> = {
  ok: 'ok',
  empty: 'empty',
  stale: 'stale',
  rate_limited: 'rate limited',
  error: 'error',
};

function niceMax(n: number): number {
  if (n <= 5) return 5;
  const mag = 10 ** Math.floor(Math.log10(n));
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * mag;
    if (candidate >= n) return candidate;
  }
  return 10 * mag;
}

export function Chart(props: { series: SeriesPoint[]; label: string }): VNode {
  const points = props.series;
  if (!points.length) {
    return html`<p class="empty">
      No days in the window. The usage store has never been written to on this host.
    </p>`;
  }

  const peak = Math.max(...points.map((p) => p.calls));
  const max = niceMax(peak);
  const plot = H - PAD_TOP - PAD_BOTTOM;
  const width = points.length * (COL + GAP) - GAP;
  const total = points.reduce((n, p) => n + p.calls, 0);
  const zeros = points.filter((p) => p.calls === 0).length;

  const bars = points.map((p, i) => {
    const x = i * (COL + GAP);
    let y = PAD_TOP + plot;
    const segments: VNode[] = [];
    for (const outcome of OUTCOMES) {
      const n = p.byOutcome[outcome];
      if (!n) continue;
      const h = Math.max(1, (n / max) * plot);
      y -= h;
      segments.push(
        html`<rect
          class=${`bar bar--${outcome}`}
          x=${String(x)}
          y=${String(y)}
          width=${String(COL)}
          height=${String(h)}
        ></rect>`,
      );
    }
    // A zero day still gets a mark on the baseline. An empty column would be
    // indistinguishable from a day the server never reported.
    if (!segments.length) {
      segments.push(
        html`<rect
          class="bar bar--zero"
          x=${String(x)}
          y=${String(PAD_TOP + plot - 1)}
          width=${String(COL)}
          height="1"
        ></rect>`,
      );
    }
    const day = p.day.slice(5);
    // The tooltip carries the split, not just the height. The colours are the
    // only thing distinguishing a day of 40 answers from a day of 40 empty
    // ones, and a colour is not a reading — anyone who wants the number of
    // hollow answers on a particular day should be able to get it here rather
    // than by matching a swatch against a legend.
    const split = OUTCOMES.filter((o) => p.byOutcome[o] > 0)
      .map((o) => `${fmtInt(p.byOutcome[o])} ${OUTCOME_LABEL[o]}`)
      .join(', ');
    return html`<g>
      <title>
        ${p.day}: ${fmtInt(p.calls)} ${p.calls === 1 ? 'call' : 'calls'}${split ? ` — ${split}` : ''}
      </title>
      ${segments}
      ${i === 0 || i === points.length - 1 || (points.length > 6 && i === Math.floor(points.length / 2))
        ? html`<text class="chart__tick" x=${String(x + COL / 2)} y=${String(H - 6)}>${day}</text>`
        : null}
    </g>`;
  });

  return html`
    <div class="chart">
      ${Scroller({
        label: props.label,
        children: html`
          <svg
            class="chart__svg"
            viewBox=${`0 0 ${width} ${H}`}
            width=${String(width)}
            height=${String(H)}
            role="img"
            aria-label=${`${props.label}: ${fmtInt(total)} calls across ${points.length} days, ${zeros} of them with no calls at all. Peak ${fmtInt(peak)} on one day.`}
          >
            <line
              class="chart__axis"
              x1="0"
              y1=${String(PAD_TOP + plot)}
              x2=${String(width)}
              y2=${String(PAD_TOP + plot)}
            ></line>
            ${bars}
          </svg>
        `,
      })}
      <p class="chart__scale">Peak ${fmtInt(peak)} calls/day · axis to ${fmtInt(max)}</p>
      <ul class="legend">
        ${OUTCOMES.map(
          (o) => html`<li class="legend__item">
            <span class=${`legend__key legend__key--${o}`} aria-hidden="true"></span>
            ${OUTCOME_LABEL[o]}
          </li>`,
        )}
      </ul>
    </div>
  `;
}
